/**
 * `createApiFeaturesSource` — the signed-in tenancy's resolved customer feature
 * flags ("is this rolled out to THEM?", `@authe/fe-commons/features`).
 *
 * Reads management-api `GET /features` (canonical `/features`, rewritten onto the
 * `.management.` zone by `RESOURCE_REWRITES`). management-api relays
 * client-config-api's resolution for the caller's OWN customer / org / tenant /
 * user, taken from the verified token — the dashboard names no ids and has no
 * client-config access of its own.
 *
 * `cacheTtlMs: 0`: the path carries no tenancy, so the ApiClient's URL-keyed GET
 * cache would serve one tenancy's answer to the next for 30s after a switch. The
 * caller (`app/DashboardFeatureFlags`) caches in react-query under a key that
 * does carry the tenancy.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import type { FeatureFlagsResponse, FeatureSource } from "@authe/fe-commons/features";

export const FEATURES_PATH = "/features";

export interface FeaturesSource {
  /** The resolved flags. Throws `ApiError` on a refused read. */
  getFeatureFlags(signal?: AbortSignal): Promise<FeatureFlagsResponse>;
}

/**
 * Coerce the wire body at the boundary: only entries with a real boolean
 * `enabled` survive, so a malformed value is "no answer" (the flag keeps its
 * default downstream), never a truthy string read as ON.
 */
export function normalizeFeatureFlagsResponse(raw: unknown): FeatureFlagsResponse {
  const body = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const wireFlags =
    body.flags && typeof body.flags === "object" && !Array.isArray(body.flags)
      ? (body.flags as Record<string, unknown>)
      : {};
  const flags: FeatureFlagsResponse["flags"] = {};
  for (const [name, value] of Object.entries(wireFlags)) {
    if (!value || typeof value !== "object") continue;
    const { enabled, source } = value as { enabled?: unknown; source?: unknown };
    if (typeof enabled !== "boolean") continue;
    flags[name] = {
      enabled,
      source: (typeof source === "string" ? source : "default") as FeatureSource,
    };
  }
  const unknown = Array.isArray(body.unknown)
    ? body.unknown.filter((name): name is string => typeof name === "string")
    : [];
  return { flags, unknown };
}

export function createApiFeaturesSource(client: ApiClient): FeaturesSource {
  return {
    async getFeatureFlags(signal?: AbortSignal): Promise<FeatureFlagsResponse> {
      const res = await client.get<unknown>(FEATURES_PATH, { cacheTtlMs: 0, signal });
      return normalizeFeatureFlagsResponse(unwrapData(res));
    },
  };
}
